import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  HelpCircle,
  Plus,
  Save,
  Trash2,
  Edit3,
  Eye,
  ChevronUp,
  ChevronDown,
  EyeOff,
  X,
} from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { renderMarkdownSafe } from "../../lib/safeMarkdown";

export default function FAQTab() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // faq object or "new"
  const [reordering, setReordering] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/faqs`);
      setFaqs(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load FAQs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (payload, faqId) => {
    try {
      if (faqId) {
        await axios.put(`${API}/admin/faqs/${faqId}`, payload);
        toast.success("FAQ updated");
      } else {
        await axios.post(`${API}/admin/faqs`, payload);
        toast.success("FAQ created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save FAQ");
    }
  };

  const handleDelete = async (faq) => {
    if (!window.confirm(`Delete FAQ "${faq.question}"?`)) return;
    try {
      await axios.delete(`${API}/admin/faqs/${faq.faq_id}`);
      toast.success("FAQ deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete FAQ");
    }
  };

  const togglePublished = async (faq) => {
    try {
      await axios.put(
        `${API}/admin/faqs/${faq.faq_id}`,
        { published: !faq.published }
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update FAQ");
    }
  };

  const moveItem = async (index, direction) => {
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= faqs.length) return;
    const newList = [...faqs];
    const [moved] = newList.splice(index, 1);
    newList.splice(newIdx, 0, moved);
    setFaqs(newList); // optimistic
    setReordering(true);
    try {
      await axios.post(
        `${API}/admin/faqs/reorder`,
        { ordered_ids: newList.map((f) => f.faq_id) }
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reorder");
      load();
    } finally {
      setReordering(false);
    }
  };

  return (
    <div data-testid="faq-tab" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[#C5A059]" /> FAQ Manager
          </h2>
          <p className="text-white/40 text-xs mt-1">
            Publicly shown at{" "}
            <a
              href="/faq"
              target="_blank"
              rel="noreferrer"
              className="text-[#C5A059] hover:underline"
            >
              /faq
            </a>
            . Markdown supported in the answer body.
          </p>
        </div>
        <Button
          onClick={() => setEditing("new")}
          className="btn-gold"
          data-testid="faq-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add FAQ
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="spinner" />
        </div>
      ) : faqs.length === 0 ? (
        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 text-center text-white/50">
          <p>No FAQs yet. Click "Add FAQ" to create your first one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {faqs.map((faq, idx) => (
            <FAQRow
              key={faq.faq_id}
              faq={faq}
              index={idx}
              total={faqs.length}
              disabled={reordering}
              onEdit={() => setEditing(faq)}
              onDelete={() => handleDelete(faq)}
              onTogglePublished={() => togglePublished(faq)}
              onMoveUp={() => moveItem(idx, -1)}
              onMoveDown={() => moveItem(idx, 1)}
            />
          ))}
        </div>
      )}

      {editing && (
        <FAQEditorModal
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={(payload) =>
            handleSave(payload, editing === "new" ? null : editing.faq_id)
          }
        />
      )}
    </div>
  );
}

function FAQRow({
  faq,
  index,
  total,
  disabled,
  onEdit,
  onDelete,
  onTogglePublished,
  onMoveUp,
  onMoveDown,
}) {
  return (
    <div
      className="bg-[#0F0F0F] border border-white/5 rounded-sm p-4 flex items-start gap-3"
      data-testid={`faq-row-${faq.faq_id}`}
    >
      <div className="flex flex-col gap-1">
        <button
          onClick={onMoveUp}
          disabled={disabled || index === 0}
          className="text-white/30 hover:text-[#C5A059] disabled:opacity-20 disabled:cursor-not-allowed"
          data-testid={`faq-move-up-${faq.faq_id}`}
          aria-label="Move up"
        >
          <ChevronUp className="w-4 h-4" />
        </button>
        <button
          onClick={onMoveDown}
          disabled={disabled || index === total - 1}
          className="text-white/30 hover:text-[#C5A059] disabled:opacity-20 disabled:cursor-not-allowed"
          data-testid={`faq-move-down-${faq.faq_id}`}
          aria-label="Move down"
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] text-white/30 font-mono">#{index + 1}</span>
          {!faq.published && (
            <span className="text-[10px] uppercase text-orange-400 bg-orange-400/10 px-2 py-0.5 rounded-sm">
              Hidden
            </span>
          )}
        </div>
        <p className="text-white font-medium truncate">{faq.question}</p>
        <p className="text-white/40 text-xs mt-1 line-clamp-2">
          {faq.content.replace(/[#*_`>-]/g, "").slice(0, 160)}
        </p>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={onTogglePublished}
          className="text-white/60 hover:text-[#C5A059]"
          data-testid={`faq-toggle-published-${faq.faq_id}`}
          title={faq.published ? "Hide from public page" : "Show on public page"}
        >
          {faq.published ? (
            <Eye className="w-4 h-4" />
          ) : (
            <EyeOff className="w-4 h-4" />
          )}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="text-white/60 hover:text-[#C5A059]"
          data-testid={`faq-edit-${faq.faq_id}`}
        >
          <Edit3 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          className="text-white/60 hover:text-red-400"
          data-testid={`faq-delete-${faq.faq_id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function FAQEditorModal({ initial, onClose, onSave }) {
  const [question, setQuestion] = useState(initial?.question || "");
  const [content, setContent] = useState(initial?.content || "");
  const [published, setPublished] = useState(
    initial ? !!initial.published : true
  );
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  const previewHtml = useMemo(
    () => ({ __html: renderMarkdownSafe(content, "faq-admin-preview") }),
    [content]
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!question.trim()) {
      toast.error("Question is required");
      return;
    }
    if (!content.trim()) {
      toast.error("Content is required");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        question: question.trim(),
        content: content.trim(),
        published,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="faq-editor-modal"
    >
      <form
        onSubmit={submit}
        className="bg-[#121212] border border-white/10 rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-lg font-semibold text-white">
            {initial ? "Edit FAQ" : "New FAQ"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-white/40 hover:text-white"
            data-testid="faq-editor-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-white/50 text-[10px] uppercase mb-1 block">
              Question / Title
            </label>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. How far in advance should I book?"
              className="input-dark"
              maxLength={500}
              data-testid="faq-editor-question"
              required
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-white/50 text-[10px] uppercase">
                Answer / Content (Markdown)
              </label>
              <button
                type="button"
                onClick={() => setShowPreview((p) => !p)}
                className="text-[#C5A059] text-xs hover:underline"
                data-testid="faq-editor-toggle-preview"
              >
                {showPreview ? "Edit" : "Preview"}
              </button>
            </div>
            {showPreview ? (
              <div
                className="bg-[#0A0A0A] border border-white/5 rounded-sm p-4 min-h-[260px] text-white/80 prose-custom prose-sm"
                dangerouslySetInnerHTML={previewHtml}
              />
            ) : (
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Your answer. You can use **bold**, lists, links..."
                className="w-full h-64 bg-[#0A0A0A] border border-[#333] rounded-sm p-4 text-white/80 text-sm font-mono resize-y focus:border-[#C5A059]/50 focus:outline-none"
                maxLength={20000}
                spellCheck="false"
                data-testid="faq-editor-content"
                required
              />
            )}
          </div>
          <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={published}
              onChange={(e) => setPublished(e.target.checked)}
              className="accent-[#C5A059]"
              data-testid="faq-editor-published"
            />
            Published (visible on public /faq page)
          </label>
        </div>
        <div className="p-5 border-t border-white/5 flex justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-white/60"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="btn-gold"
            data-testid="faq-editor-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
}
